/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { LoggerStore } from "../../logger/logger-store"
import type { Result } from "../../network/http-client"
import { makeGetRequest } from "../../network/http-client"

import type { ApkDetailStore } from "./apk-detail-store"
import type { ApkDetail } from "./apk-detail-types"

/**
 * Console API paths used by the service.
 */
enum Endpoint {
    apkDetail = "/api/private/apk/:apkId"
}

/**
 * Loads APK details into the view-model store.
 *
 * Successful responses are stored; failures are logged as toasts. The result
 * is also returned so callers can react to the request outcome.
 */
export class ApkDetailService {
    /**
     * Placeholder in `Endpoint.apkDetail` for the APK ID.
     */
    private readonly apkIdPlaceholder = ":apkId"

    constructor(
        private readonly store: ApkDetailStore,
        private readonly logger: LoggerStore
    ) {}

    /**
     * Fetches and stores the details for an APK.
     *
     * Failed requests leave the existing detail unchanged and are logged.
     *
     * @param apkId - Backend `iap_apk.id` of the APK to load.
     * @returns The request result.
     */
    async getApkDetail(apkId: number): Promise<Result<ApkDetail>> {
        const detail = await makeGetRequest<ApkDetail>(
            Endpoint.apkDetail.replace(this.apkIdPlaceholder, `${apkId}`),
            {}
        )
        if (detail.success && detail.data) {
            this.store.setDetail(detail.data)
            return detail
        }
        if (!detail.success) {
            this.logger.logApiError("Get package detail failed", detail.apiError, detail.error)
        }
        return detail
    }
}
